import { buildFlagOptions } from "../../components/visuals/StatusFlag";
import { MESSAGES } from "../../messages";
import {
  Alignment,
  Proposal,
  Vote,
  VotingColumnType,
} from "../../models/stewardshipModels";
import {
  CellType,
  ColumnType as CustomColumnType,
  EngagementDataSourceType,
  FlaggableVote,
  TableCellType,
  VotingDataSourceType,
} from "../../models/tableModels";
import { ALIGNMENT_FILTERS, ENGAGEMENT_COLUMN_TYPES } from "./constants";
import { statusBadgeColors } from "../../styles";
import { TagProps } from "../../components/visuals/CompassTag";
import { VOTER_SUFFIX } from "../../constants";
import { isNotNan } from "../../utils/formattingUtils";

export const getColumnTypeFromKeyOrValue = (
  column: string,
  value: CellType,
  columnTypes: CustomColumnType[] = ENGAGEMENT_COLUMN_TYPES,
  loading = false,
): CustomColumnType => {
  if (loading) return { columnKey: column, type: TableCellType.LOADING };
  if (value === MESSAGES.NOT_AVAILABLE)
    return { columnKey: column, type: TableCellType.TEXT };
  if (column.includes(VOTER_SUFFIX))
    return { columnKey: column, type: TableCellType.FLAGGABLE_VOTE };

  for (const columnType of columnTypes) {
    if (columnType.columnKey === column) {
      return columnType;
    }
  }

  // Fallback based on the type of value
  if (typeof value === "number") {
    return { columnKey: column, type: TableCellType.NUMBER };
  }
  return { columnKey: column, type: TableCellType.TEXT };
};

const checkIsManagerVoteColumn = (columnKey: string): boolean => {
  return columnKey.includes(VOTER_SUFFIX);
};

export const getUniqueFilters = (
  data: (EngagementDataSourceType | VotingDataSourceType)[],
  key: string,
): { text: string; value: string | number | boolean }[] => {
  if (checkIsManagerVoteColumn(key)) {
    return ALIGNMENT_FILTERS;
  }
  const uniqueValues = Array.from(
    //eslint-disable-next-line
    // @ts-ignore
    new Set(data.map((item) => item[key])),
  );
  return uniqueValues
    .map((value) => ({
      text: value as string,
      value: value as string | number,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));
};

export const applyFilter = (
  record: EngagementDataSourceType | VotingDataSourceType,
  value: string,
  key: string,
): boolean => {
  if (checkIsManagerVoteColumn(key)) {
    const managerId = key.replace(VOTER_SUFFIX, "");
    //eslint-disable-next-line
    // @ts-ignore
    const vote = record[managerId]?.vote;
    const isAligned: boolean =
      (record as VotingDataSourceType).alignmentVote === vote;
    const alignment: Alignment = isAligned
      ? Alignment.Aligned
      : Alignment.Misaligned;
    return value === alignment;
  }

  //eslint-disable-next-line
  // @ts-ignore
  return (record[key] as unknown as string).includes(value);
};

const prioritizeVoter = (
  proposals: Proposal[],
  prioritizedVoterId: string,
): Proposal[] => {
  const matchingVoter = proposals.filter(
    (item) => item.voter_id === prioritizedVoterId,
  );
  const others = proposals.filter(
    (item) => item.voter_id !== prioritizedVoterId,
  );
  return [...matchingVoter, ...others];
};

const getAlignmentVote = (
  alignmentVoterId: string | null,
  proposal: Proposal,
): Vote | "-" => {
  if (!alignmentVoterId) return proposal.management_recommendation;
  return proposal.voter_id === alignmentVoterId ? proposal.vote_type : "-";
};

const getPropsFromVotes = (vote: Vote, alignmentVote: Vote | "-"): TagProps => {
  if (alignmentVote === MESSAGES.NOT_AVAILABLE) {
    return {
      tagKey: vote,
      displayValue: vote,
      bg: statusBadgeColors.Planned.bg,
      color: statusBadgeColors.Planned.text,
    };
  }
  return {
    tagKey: vote,
    displayValue: vote,
    bg:
      vote === alignmentVote
        ? statusBadgeColors.Complete.bg
        : statusBadgeColors.Stuck.bg,
    color:
      vote === alignmentVote
        ? statusBadgeColors.Complete.text
        : statusBadgeColors.Stuck.text,
  };
};

export const buildVotingDataSource = (
  votingData: Proposal[],
  alignmentVoterId: string | null = null,
): VotingDataSourceType[] => {
  if (alignmentVoterId) {
    votingData = prioritizeVoter(votingData, alignmentVoterId);
  }

  const dataSource: VotingDataSourceType[] = [];

  votingData.forEach((proposal) => {
    // Check if the proposal already exists in the dataSource
    let existingProposal = dataSource.find(
      (res) => res.proposalId === proposal.proposal_id,
    );

    // If the proposal doesn't exist, create a new entry
    if (!existingProposal) {
      existingProposal = {
        proposalId: proposal.proposal_id,
        proposalDescription: proposal.proposal_description,
        proposalType: proposal.proposal_type,
        managementRecommendation: {
          color: statusBadgeColors.Planned.text,
          bg: statusBadgeColors.Planned.bg,
          displayValue: "",
          tagKey: proposal.management_recommendation,
          shouldDisplayKey: true,
        },
        key: proposal.proposal_id,
        alignmentVote: getAlignmentVote(alignmentVoterId, proposal),
      };

      dataSource.push(existingProposal);
    }

    existingProposal[proposal.voter_id] = {
      votingRecordId: proposal.voting_record_id,
      vote: proposal.vote_type,
      managerName: proposal.voter_name,
      options: buildFlagOptions(proposal.flag_status),
      isInTable: true,
      hasRationale: isNotNan(proposal.rationale),
      rationale: isNotNan(proposal.rationale) ? proposal.rationale : undefined,
      ...getPropsFromVotes(proposal.vote_type, existingProposal.alignmentVote),
    } as FlaggableVote;
  });

  return dataSource;
};

export const getDynamicColumns = (data: Proposal[]): VotingColumnType[] => {
  const uniqueManagers = new Set();

  const resultArray = data.reduce((acc: VotingColumnType[], item: Proposal) => {
    if (!uniqueManagers.has(item.voter_id)) {
      uniqueManagers.add(item.voter_id);
      acc.push({
        key: `${item.voter_id}${VOTER_SUFFIX}`,
        title: item.voter_name,
        dataIndex: item.voter_id,
        hasFilters: true,
      });
    }
    return acc;
  }, []);

  return resultArray;
};
